
/*
 * Recently viewed products.
 *
 * Two independent jobs, both driven off the DOM so the file can be loaded on any template:
 *   1. Record — a [data-rv-record] node pushes its handle to the front of the stored list.
 *   2. Render — a [data-rv-list] node fetches cards through the Section Rendering API.
 */

(function () {
  'use strict';

  const RV_KEY = 'mrs:recently-viewed';
  const RV_MAX = 12;

  function rvRead() {
    try {
      const raw = window.localStorage.getItem(RV_KEY);
      if (!raw) return [];

      const parsed = JSON.parse(raw);

      return Array.isArray(parsed)
        ? parsed.filter((handle) => typeof handle === 'string')
        : [];
    } catch (error) {
      return [];
    }
  }

  function rvWrite(handles) {
    try {
      window.localStorage.setItem(
        RV_KEY,
        JSON.stringify(handles.slice(0, RV_MAX))
      );
    } catch (error) {
      /* Storage disabled or full — recording is best effort only. */
    }
  }

  function rvRecord() {
    const node = document.querySelector('[data-rv-record]');
    if (!node) return;

    const handle = node.dataset.handle;
    if (!handle) return;

    const handles = rvRead().filter((stored) => stored !== handle);
    handles.unshift(handle);

    rvWrite(handles);
  }

  async function rvFetchCard(rootUrl, handle, cardSection) {
    const base = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;

    const url = `${base}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(cardSection)}`;

    const response = await fetch(url);
    if (!response.ok) return null;

    const html = new DOMParser().parseFromString(
      await response.text(),
      'text/html'
    );

    const card = html.querySelector('[data-rv-card]');
    if (!card) return null;

    const related = html.querySelector('template[data-rv-related]');

    card.rvRelated = related
      ? Array.from(related.content.querySelectorAll('[data-rv-card]'))
      : [];

    return card;
  }

  async function rvIsListed(rootUrl, card) {
    const title = card.dataset.title;
    if (!title) return true;

    const base = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;

    const params = new URLSearchParams({
      q: title,
      'resources[type]': 'product',
      'resources[limit]': '10',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title',
    });

    try {
      const response = await fetch(
        `${base}search/suggest.json?${params.toString()}`
      );

      if (!response.ok) return true;

      const data = await response.json();

      const products =
        (data &&
          data.resources &&
          data.resources.results &&
          data.resources.results.products) ||
        [];

      return products.some(
        (product) => product.handle === card.dataset.handle
      );
    } catch (error) {
      return true;
    }
  }

  function rvInterleave(groups) {
    const out = [];
    const longest = Math.max(0, ...groups.map((group) => group.length));

    for (let i = 0; i < longest; i += 1) {
      groups.forEach((group) => {
        if (group[i]) out.push(group[i]);
      });
    }

    return out;
  }

  async function rvFetchRecommendations(
    rootUrl,
    productId,
    cardSection,
    limit
  ) {
    const base = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;

    const url = `${base}recommendations/products?product_id=${encodeURIComponent(productId)}&limit=${limit}&intent=related&section_id=${encodeURIComponent(cardSection)}`;

    const response = await fetch(url);
    if (!response.ok) return [];

    const html = new DOMParser().parseFromString(
      await response.text(),
      'text/html'
    );

    return Array.from(html.querySelectorAll('[data-rv-card]'));
  }

  function rvRefreshSlider(list) {
    const root = list.closest('[data-featured-collection-tabs]');
    if (!root) return;

    if (typeof root.fctResize === 'function') {
      root.fctResize();
      return;
    }

    window.addEventListener(
      'load',
      () => {
        if (typeof root.fctResize === 'function') {
          root.fctResize();
        }
      },
      { once: true }
    );
  }

  async function rvRender() {
    const list = document.querySelector('[data-rv-list]');
    if (!list) return;

    const section = list.closest('[data-rv-section]');
    const rootUrl = list.dataset.rootUrl || '/';
    const limit = parseInt(list.dataset.limit, 10) || 4;
    const exclude = list.dataset.exclude || '';
    const cardSection =
      list.dataset.cardSection || 'recently-viewed-card';
    const itemClass = list.dataset.itemClass || 'rv-item';

    const handles = rvRead().filter((handle) => handle !== exclude);

    if (handles.length === 0) return;

    const cards = await Promise.all(
      handles.map((handle) =>
        rvFetchCard(rootUrl, handle, cardSection).catch(() => null)
      )
    );

    const fetched = cards.filter(Boolean);

    const listed = await Promise.all(
      fetched.map((card) => rvIsListed(rootUrl, card))
    );

    const found = fetched
      .filter((_card, index) => listed[index])
      .slice(0, limit);

    if (found.length === 0) return;

    const useListItems = list.tagName === 'UL';

    const wrap = (card) => {
      const item = document.createElement(useListItems ? 'li' : 'div');
      item.className = itemClass;
      item.appendChild(card);
      return item;
    };

    const items = found.map(wrap);

    if (items.length < limit) {
      const used = new Set([exclude, ...handles]);

      const recommended = await Promise.all(
        found.map((card) =>
          card.dataset.productId
            ? rvFetchRecommendations(
                rootUrl,
                card.dataset.productId,
                cardSection,
                limit
              ).catch(() => [])
            : []
        )
      );

      const candidates = [
        ...rvInterleave(recommended),
        ...rvInterleave(found.map((card) => card.rvRelated || [])),
      ];

      candidates.forEach((card) => {
        if (items.length >= limit || used.has(card.dataset.handle)) return;

        used.add(card.dataset.handle);
        items.push(wrap(card));
      });
    }

    const fragment = document.createDocumentFragment();

    items.forEach((item) => fragment.appendChild(item));

    list.innerHTML = '';
    list.appendChild(fragment);

    if (section) section.hidden = false;

    rvRefreshSlider(list);
  }

  function rvInit() {
    rvRecord();
    rvRender();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', rvInit, { once: true });
  } else {
    rvInit();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-rv-list]')) {
      rvRender();
    }
  });
})();
